import { Box, Button, Flex, Icon, Progress, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight } from "react-icons/fi";

export interface NowPanelAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
}

export interface NowPanelProps {
  /** Already translated: the component runs no i18n over what it is handed. */
  status: ReactNode;
  detail?: ReactNode;
  progress?: { done: number; total: number } | null;
  action?: NowPanelAction | null;
  secondaryAction?: NowPanelAction | null;
  label?: string;
}

export const NowPanel = ({
  status,
  detail,
  progress,
  action,
  secondaryAction,
  label,
}: NowPanelProps) => {
  const { t } = useTranslation();

  return (
    <Box
      mt="6"
      bg="smPrimarySofter"
      rounded="xl"
      border="1px solid"
      borderColor="smPrimarySofterBorder"
      px={{ base: "4", md: "6" }}
      py={{ base: "4", md: "5" }}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
      >
        <Flex align="flex-start" gap={{ base: "3", md: "5" }} minW="0">
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="smMuted"
            textTransform="uppercase"
            letterSpacing="wider"
            pt={{ base: "0.5", md: "1" }}
            flexShrink={0}
          >
            {label ?? t("Now")}
          </Text>
          <Box minW="0">
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="smText">
              {status}
            </Text>
            {detail && (
              <Text fontSize="md" color="smMuted" mt="0.5">
                {detail}
              </Text>
            )}
            {progress && (
              <Progress
                value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
                aria-label={String(t("Progress"))}
                size="sm"
                rounded="full"
                colorScheme="teal"
                mt="3"
                maxW="sm"
                sx={{ "& > div": { borderRadius: "full" } }}
              />
            )}
          </Box>
        </Flex>
        {(action || secondaryAction) && (
          <Flex direction={{ base: "column", sm: "row" }} gap="3" flexShrink={0}>
            {secondaryAction && (
              <Button
                variant="outline"
                colorScheme="teal"
                size="lg"
                onClick={secondaryAction.onClick}
                isLoading={secondaryAction.isLoading}
              >
                {secondaryAction.label}
              </Button>
            )}
            {action && (
              <Button
                colorScheme="teal"
                size="lg"
                rightIcon={<Icon as={FiArrowRight} />}
                onClick={action.onClick}
                isLoading={action.isLoading}
              >
                {action.label}
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
